import { resolveAuthUserDocument } from '../services/authUserIdentity';
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  where,
  serverTimestamp
} from "firebase/firestore";
import { fetchPublicRegistrations } from "../utils/publicRegistrations";
import { testFirestoreConnection } from "../services/testFirestore";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { initialFormData } from "../utils/validation";
import {
  validatePersonalInfo,
  validateRaceDetails,
  validateReviewSubmit,
  validateAll,
  markStepFieldsTouched,
  RaceValidationContext,
  LICENSE_NUMBER_REGEX,
} from "../utils/registrationValidation";
import { useEventEdition, CurrentEvent, DEFAULT_REGISTRATION_CONFIG, RegistrationConfig } from "../contexts/EventEditionContext";
import { useEditionFromUrl } from "../hooks/useEditionFromUrl";
import PersonalInfoForm from "../components/registration/PersonalInfoForm";
import RaceDetailsForm from "../components/registration/RaceDetailsForm";
import ReviewRegistration from "../components/registration/ReviewRegistration";
import RegistrationStepper from "../components/registration/RegistrationStepper";
import RegistrationSnackbar from "../components/registration/RegistrationSnackbar";
import { RegistrationHelpSection } from "../components/common/RegistrationHelpSection";
import { db } from "../config/firebase";
import { useRegistrationDraft } from "../hooks/useRegistrationDraft";
import { rhAnalytics } from "../services/analyticsService";
import { checkPreRegistrationEligibility, isPreRegistrationActive, EligibilityResult } from "../services/registrationEligibilityService";

// Helper function to get user language preference
export async function getUserLanguagePreferenceFromFirestore(email: string): Promise<string | null> {
  try {
    if (process.env.REACT_APP_PERSON_IDENTITY_POLICY === 'strict-v1') {
      const authUser = getAuth().currentUser;
      if (!authUser) return null;
      const profile = await resolveAuthUserDocument(db, authUser.uid);
      return profile?.data()?.languagePreference || null;
    }

    const usersQuery = query(collection(db, 'users'), where('email', '==', email));
    const userSnap = await getDocs(usersQuery);
    
    if (!userSnap.empty) {
      const user = userSnap.docs[0].data();
      return user.languagePreference || null;
    }
    
    return null;
  } catch (error) {
    console.warn('Error getting user language preference:', error);
    return null;
  }
}


// Inner component containing all hooks and logic, now receiving event as prop
const RegistrationPageInner: React.FC<{ event: CurrentEvent }> = ({
  event,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showWaitlistInfoOnLoad = searchParams.get('wlinfo') === '1';
  
  const steps = [
    t('registration.steps.personalInfo'),
    t('registration.steps.raceDetails'),
    t('registration.steps.reviewSubmit')
  ];
  // Firebase Auth state
  const [user, setUser] = useState<any>(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Pre-registration eligibility
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState<EligibilityResult | null>(null);

  // Prefill/edit registration state
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [existingRegistrationId, setExistingRegistrationId] = useState<
    string | null
  >(null);
  const [prefillChecked, setPrefillChecked] = useState(false);
  const [profilePrefillChecked, setProfilePrefillChecked] = useState(false);
  const [updateRunnerProfile] = useState(true); // setUpdateRunnerProfile available if needed for future use
  const [representingOptions, setRepresentingOptions] = useState<string[]>([]);

  // Form state
  const [activeStep, setActiveStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0])); // Track which steps user has visited
  const [stepErrors, setStepErrors] = useState<Record<number, boolean>>({}); // Track which steps have validation errors
  const [formData, setFormData] = useState({
    ...initialFormData,
    isOnWaitinglist: false,
    waitinglistExpires: null as Date | null
  });
  const formDataRef = useRef<any>(formData);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touchedFields, setTouchedFields] = useState<{
    [key: string]: boolean;
  }>({});
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [openRegCount, setOpenRegCount] = useState(0);
  const [forcedWaitlistDialogOpen, setForcedWaitlistDialogOpen] = useState(false);
  const [waitlistInfoDialogOpen, setWaitlistInfoDialogOpen] = useState(false);
  const waitlistInfoShownRef = useRef(false);
  // Resolve the URL to return to when Cancel is pressed.
  // Prefer the explicit ?from= param (set by krultra.no buttons); fall back to document.referrer, then home.
  const callerUrlRef = useRef<string>(
    searchParams.get('from') ||
    (typeof document !== 'undefined' && document.referrer ? document.referrer : '/')
  );
//  const [waitingListConsent, setWaitingListConsent] = useState(false);

  // Snackbar state
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "info" | "warning"
  >("info");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  
  // Refund dialog state (requires user acknowledgment)
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundDialogMessage, setRefundDialogMessage] = useState("");
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Draft system - auto-save to Firestore (disabled when editing existing registration)
  const {
    isDraftLoading,
    loadedDraft,
    deleteDraft
  } = useRegistrationDraft({
    eventId: event.id,
    odId: user?.uid || '',
    formData,
    currentStep: activeStep,
    visitedSteps,
    enabled: !isEditingExisting && !!user?.uid && authChecked && prefillChecked
  });

  // Field refs for input elements
  const fieldRefs = useRef<{
    isOnWaitinglist: HTMLInputElement | null;
    waitinglistExpires: HTMLInputElement | null;
    termsAccepted: HTMLInputElement | null;
  }>({ isOnWaitinglist: null, waitinglistExpires: null, termsAccepted: null });

  // Div refs for container elements
  const divRefs = useRef<
    Record<string, React.RefObject<HTMLDivElement | null>>
  >({});

  // Get registration config with defaults
  const registrationConfig: RegistrationConfig = useMemo(() => ({
    fields: {
      ...DEFAULT_REGISTRATION_CONFIG.fields,
      ...event.registrationConfig?.fields
    }
  }), [event.registrationConfig?.fields]);

  // Compute whether license is required for the selected race distance
  const selectedDistance = event.raceDistances?.find(d => d.id === formData.raceDistance);
  const licenseFee = (event.fees as any)?.oneDayLicense ?? event.fees?.oneTimeLicense ?? 0;
  
  const distanceNameEn = (selectedDistance?.displayName ?? '').toLowerCase();
  const distanceNameNo = (selectedDistance?.displayName_no ?? '').toLowerCase();
  const distanceId = (selectedDistance?.id ?? '').toLowerCase();
  
  // Hiking class detection - explicitly exclude from license requirement
  const isHikingClass =
    distanceNameEn.includes('hiking') ||
    distanceNameEn.includes('hike') ||
    distanceNameNo.includes('turklasse') ||
    distanceNameNo.includes('tur') ||
    distanceId.includes('hiking') ||
    distanceId.includes('hike') ||
    distanceId.includes('tur');
  
  // Competition class detection
  const isCompetitionClass =
    distanceNameEn.includes('competition') ||
    distanceNameNo.includes('konkurranse') ||
    distanceId.includes('competition') ||
    distanceId.includes('konkurranse') ||
    distanceId === 'comp';
  
  // Timed recreation class detection (requires license like competition)
  // Matches: "Timed recreation", "Trim med tidtaking", "trim_tidtaking", etc.
  const isTimedRecreationClass =
    distanceNameEn.includes('timed') ||
    distanceNameNo.includes('tidtaking') ||
    distanceId.includes('timed') ||
    distanceId.includes('tidtaking') ||
    distanceId === 'trim' ||
    distanceId === 'trim_tidtaking' ||
    distanceId === 'timedrecreation' ||
    distanceId === 'timed_recreation';
  
  // License required for Competition and Timed Recreation, but NOT Hiking (matches RaceDetailsForm)
  const requiresLicenseBase =
    !isHikingClass &&
    (licenseFee > 0 || isCompetitionClass || isTimedRecreationClass);

  // Calculate age-based license exemption for Malvikingen Opp
  const ageExemption = useMemo(() => {
    // Only apply to Malvikingen Opp events
    if (!event.id.includes('mo') && !event.eventShortName?.toLowerCase().includes('malvikingen')) {
      return null;
    }

    // Must have either dateOfBirth or yearOfBirth, and event end time
    if (!event.endTime) {
      return null;
    }

    // If both dateOfBirth and yearOfBirth are missing, assume license is required
    if (!formData.dateOfBirth && !(formData as any).yearOfBirth) {
      return null;
    }

    try {
      const eventEndDate = new Date(event.endTime);
      
      // Check if event date is valid
      if (isNaN(eventEndDate.getTime())) {
        return null;
      }

      const eventYear = eventEndDate.getFullYear();
      let actualAge: number | null = null;

      // Try to calculate exact age using dateOfBirth first
      if (formData.dateOfBirth) {
        const birthDate = typeof formData.dateOfBirth === 'object' && formData.dateOfBirth instanceof Date 
          ? formData.dateOfBirth 
          : new Date(formData.dateOfBirth);
        
        // Check if birth date is valid
        if (!isNaN(birthDate.getTime())) {
          // Calculate exact age on event day
          const ageOnEventDay = eventYear - birthDate.getFullYear();
          const monthDiff = eventEndDate.getMonth() - birthDate.getMonth();
          
          // Adjust age if birthday hasn't occurred yet this year
          actualAge = monthDiff < 0 || (monthDiff === 0 && eventEndDate.getDate() < birthDate.getDate()) 
            ? ageOnEventDay - 1 
            : ageOnEventDay;
        }
      }

      // If we couldn't calculate exact age, try using yearOfBirth as fallback
      if (actualAge === null && (formData as any).yearOfBirth) {
        actualAge = eventYear - (formData as any).yearOfBirth;
      }

      // If we still couldn't determine age, assume license is required
      if (actualAge === null) {
        return null;
      }

      // Check for under 13 exemption
      if (actualAge < 13) {
        return {
          type: 'under_13' as const,
          age: actualAge
        };
      }

      // Check for 81+ exemption (if they turn 81 the same year as the event)
      if (actualAge >= 81) {
        return {
          type: 'over_80' as const,
          age: actualAge
        };
      }

      return null; // No exemption, license required
    } catch (error) {
      console.warn('Error calculating age exemption:', error);
      return null;
    }
  }, [event.id, event.eventShortName, event.endTime, formData]);

  // Final determination: license required only if base requirement AND no age exemption
  const requiresLicense = requiresLicenseBase && !ageExemption;

  const eventYear = useMemo(() => {
    const d = event?.startTime instanceof Date ? event.startTime : new Date(event?.startTime as any);
    const year = d && !Number.isNaN(d.getTime()) ? d.getFullYear() : new Date().getFullYear();
    return year;
  }, [event?.startTime]);

  const calculatedPaymentRequired = useMemo(() => {
    // A settled post-race deposit must not become a new debt when contact details
    // are edited later. Preserve the audited obligation after this final settlement.
    if (formData.depositRefundCredit > 0) return formData.paymentRequired;
    // Get participation fee from distance.fee (not .fees) or fallback to event.fees.participation
    const participationFee = selectedDistance?.fee ?? event.fees?.participation ?? 0;
    
    // Get one-day license fee from event.fees.oneDayLicense (not oneTimeLicense)
    const oneTimeLicenseFee = (event.fees as any)?.oneDayLicense ?? event.fees?.oneTimeLicense ?? 0;
    
    // Service and deposit fees from event level
    const serviceFee = event.fees?.service ?? (event.fees as any)?.baseCamp ?? 0;
    const depositFee = event.fees?.deposit ?? 0;
    
    // Only charge license fee if license is required AND user doesn't have year license
    // License is NOT required for hiking classes or if age exemption applies
    const actualLicenseFee = (requiresLicense && oneTimeLicenseFee > 0 && formData.hasYearLicense !== true) ? oneTimeLicenseFee : 0;
    
    const total = participationFee + actualLicenseFee + serviceFee + depositFee;
    
    return total;
  }, [event.fees, formData.hasYearLicense, formData.raceDistance, formData.depositRefundCredit, formData.paymentRequired, selectedDistance, requiresLicense]);

  useEffect(() => {
    if (calculatedPaymentRequired > 0 && formData.paymentRequired !== calculatedPaymentRequired) {
      setFormData((prev: any) => ({ ...prev, paymentRequired: calculatedPaymentRequired }));
    }
  }, [calculatedPaymentRequired, formData.paymentRequired]);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Initialize language preference based on user data and current UI language
  useEffect(() => {
    const initializeLanguagePreference = async () => {
      if (!user?.email) return;
      
      try {
        // First check if user already has a language preference in Firestore
        const userPreference = await getUserLanguagePreferenceFromFirestore(user.email);
        
        if (userPreference) {
          // Use user's existing preference
          setFormData(prev => ({ ...prev, languagePreference: userPreference as 'en' | 'no' }));
        } else {
          // Check current UI language (i18next)
          const currentLanguage = t('language.code', { defaultValue: 'en' });
          
          // If UI language is Norwegian, default to 'no', otherwise default to 'en'
          const defaultPreference = currentLanguage === 'no' ? 'no' : 'en';
          setFormData(prev => ({ ...prev, languagePreference: defaultPreference }));
        }
      } catch (error) {
        console.warn('Error initializing language preference:', error);
        // Fallback to English
        setFormData(prev => ({ ...prev, languagePreference: 'en' }));
      }
    };

    initializeLanguagePreference();
  }, [user?.email, t]);

  // Validation context for race-specific settings (passed explicitly to validators)
  const validationContext: RaceValidationContext = useMemo(() => ({
    requiresLicense,
    eventYear,
    registrationConfig,
    ageExemption,
    eventId: event.id,
    eventShortName: event.eventShortName
  }), [requiresLicense, eventYear, registrationConfig, ageExemption, event.id, event.eventShortName]);

  // Reset validation state when switching to Race Details step
  useEffect(() => {
    if (activeStep === 1) { // Race Details step
      setTouchedFields(prev => ({
        ...prev,
        raceDistance: false,
        travelRequired: false,
        hasYearLicense: false,
        licenseNumber: false
      }));
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors.raceDistance;
        delete newErrors.travelRequired;
        delete newErrors.hasYearLicense;
        delete newErrors.licenseNumber;
        return newErrors;
      });
    }
  }, [activeStep]);

  // Analytics tracking for registration funnel
  useEffect(() => {
    // Track registration start (only on first visit to step 0)
    if (activeStep === 0 && !visitedSteps.has(1) && !visitedSteps.has(2)) {
      rhAnalytics.trackRegistrationEvent('registration_start', {
        eventId: event.id,
        step: activeStep
      });
    }
    
    // Track step changes
    if (visitedSteps.has(activeStep)) {
      rhAnalytics.trackRegistrationEvent('step_enter', {
        eventId: event.id,
        step: activeStep
      });
    }
  }, [activeStep, event.id, visitedSteps]);

  // Handlers
  const handleFormChange = useCallback((field: string, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      formDataRef.current = next;
      return next;
    });
    setTouchedFields((prev) => ({ ...prev, [field]: true }));

    if (activeStep === 1) {
      setErrors((prev) => {
        const nextErrors = { ...prev };

        if (field === 'raceDistance' && value) {
          delete nextErrors.raceDistance;
        }

        if (field === 'travelRequired' && value !== '' && value !== null && value !== undefined) {
          delete nextErrors.travelRequired;
        }

        if (field === 'hasYearLicense') {
          delete nextErrors.hasYearLicense;
          if (!value) {
            delete nextErrors.licenseNumber;
          }
        }

        if (field === 'licenseNumber' && value) {
          delete nextErrors.licenseNumber;
        }

        return nextErrors;
      });
    }
  }, [activeStep]);

  const handleFieldTouch = (field: string) => {
    // Suppress blur-triggered validation when a dialog is open — MUI dialogs steal
    // focus on mount, firing a blur on the active field before the user has left it.
    if (waitlistInfoDialogOpen || forcedWaitlistDialogOpen) return;
    setTouchedFields((prev) => ({ ...prev, [field]: true }));

    const validateTouchedField = () => {
      const currentFormData = formDataRef.current;

      if (field) {
        const options = { touchedFields: { [field]: true } };

        let fieldErrors: Record<string, string> = {};
        const personalInfoFields = ['firstName', 'lastName', 'dateOfBirth', 'nationality', 'email', 'phoneCountryCode', 'phoneNumber', 'representing', 'languagePreference'];
        const raceDetailsFields = ['raceDistance', 'hasYearLicense', 'licenseNumber', 'travelRequired', 'comments'];

        if (personalInfoFields.includes(field)) {
          fieldErrors = validatePersonalInfo(currentFormData, options, validationContext);
        } else if (raceDetailsFields.includes(field)) {
          fieldErrors = validateRaceDetails(currentFormData, validationContext, options);
        } else {
          fieldErrors = validateReviewSubmit(currentFormData, options);
        }

        const fieldValue = currentFormData[field as keyof typeof currentFormData];
        const isEmpty = fieldValue === '' || fieldValue === null || fieldValue === undefined;

        setErrors(prev => {
          const newErrors = { ...prev };
          if (field in fieldErrors) {
            newErrors[field] = fieldErrors[field];
          } else {
            delete newErrors[field];
          }
          return newErrors;
        });

        if (isEmpty) {
          setValidationAttempted(true);
        }
      }
    };

    const shouldDeferValidation =
      activeStep === 1 &&
      (field === 'raceDistance' || field === 'travelRequired' || field === 'hasYearLicense');

    if (shouldDeferValidation) {
      setTimeout(validateTouchedField, 0);
    } else {
      validateTouchedField();
    }
  };

  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);

  const showCurrentStepErrors = useCallback(() => {
    const options = { showAllErrors: true };
    let currentErrors: Record<string, string> = {};
    
    if (activeStep === 0) {
      currentErrors = validatePersonalInfo(formData, options, validationContext);
    } else if (activeStep === 1) {
      currentErrors = validateRaceDetails(formData, validationContext, options);
    } else {
      currentErrors = validateReviewSubmit(formData, options);
    }
    
    setErrors(currentErrors);
    return Object.keys(currentErrors).length > 0;
  }, [formData, validationContext, activeStep]);

  // const hasPersonalInfoErrors = useCallback(() => { // Available if needed for future use
  //   const errors = validatePersonalInfo(formData, { showAllErrors: true }, validationContext);
    
  //   // Store these errors for display when showing current step errors
  //   if (Object.keys(errors).length > 0 && activeStep === 0) {
  //     setErrors(errors);
  //   }
    
  //   return Object.keys(errors).length > 0;
  // }, [formData, activeStep]);

  // Validate a specific step and return whether it has errors
  const validateStep = useCallback((stepIndex: number): boolean => {
    const options = { showAllErrors: true };
    let stepValidationErrors: Record<string, string> = {};
    
    if (stepIndex === 0) {
      stepValidationErrors = validatePersonalInfo(formData, options, validationContext);
    } else if (stepIndex === 1) {
      stepValidationErrors = validateRaceDetails(formData, validationContext, options);
    } else if (stepIndex === 2) {
      stepValidationErrors = validateReviewSubmit(formData, options);
    }
    
    return Object.keys(stepValidationErrors).length > 0;
  }, [formData, validationContext]);

  // Validate all visited steps and update stepErrors state
  const validateVisitedSteps = useCallback(() => {
    const newStepErrors: Record<number, boolean> = {};
    
    visitedSteps.forEach((stepIndex) => {
      newStepErrors[stepIndex] = validateStep(stepIndex);
    });
    
    setStepErrors(newStepErrors);
  }, [visitedSteps, validateStep]);


  // Check if event is full
  useEffect(() => {
    if (!event) return;
    const loadCount = async () => {
      try {
        const regs = await fetchPublicRegistrations(event.id);
        const openRegs = regs.filter(
          (r) =>
            !r.isOnWaitinglist &&
            (r.status === "pending" || r.status === "confirmed"),
        );
        const waitingRegs = regs.filter(
          (r) =>
            r.isOnWaitinglist &&
            (r.status === "pending" || r.status === "confirmed"),
        );
        setOpenRegCount(openRegs.length);
        // Only consider waiting list for events that support it (not MO events)
        const eventSupportsWaitingList = !event.eventId?.startsWith('mo');
        setIsFull(
          openRegs.length >= (event.maxParticipants ?? 0) ||
            (eventSupportsWaitingList && waitingRegs.length > 0),
        );
      } catch (err) {
        console.error("Error checking registration count:", err);
      }
    };
    loadCount();
  }, [event, event?.id]);

  // Show waitlist info popup once when the event is detected as full for a new registration,
  // but only when the caller passed ?wlinfo=1 (e.g. external "Register" buttons on krultra.no).
  // Wait for prefillChecked so isEditingExisting is settled before we decide to show the popup.
  useEffect(() => {
    if (showWaitlistInfoOnLoad && isFull && prefillChecked && !isEditingExisting && !waitlistInfoShownRef.current) {
      waitlistInfoShownRef.current = true;
      setWaitlistInfoDialogOpen(true);
    }
  }, [showWaitlistInfoOnLoad, isFull, prefillChecked, isEditingExisting]);

  // Handle waiting list expiration date when event is full
  useEffect(() => {
    if (!event) return;
    if (isFull && !isEditingExisting) {
      setFormData((prev) => ({
        ...prev,
        isOnWaitinglist: true,
        waitinglistExpires: prev.waitinglistExpires ?? event.startTime,
      }));
    }
  }, [isFull, event, event.startTime, isEditingExisting]);

  // Fetch and prefill existing registration
  useEffect(() => {
    let isMounted = true;
    const fetchAndPrefill = async () => {
      if (!authChecked) return;
      if (isMounted) setPrefillChecked(false);

      if (user && user.uid) {
        try {
          const { getRegistrationsByUserId } = await import(
            "../services/registrationService"
          );
          let registrations = await getRegistrationsByUserId(
            user.uid,
            event.id
          );

          if ((!registrations || registrations.length === 0) && user.email) {
            const { getRegistrationsByEmail } = await import(
              "../services/registrationService"
            );
            registrations = await getRegistrationsByEmail(user.email, event.id);
          }
          if (registrations && registrations.length > 0) {
            const reg = registrations[0];
            if (!isMounted) return;
            setFormData(prev => ({
              ...initialFormData,
              ...reg,
              email: user.email || reg.email || "",
              dateOfBirth: reg.dateOfBirth ? new Date(reg.dateOfBirth) : null,
              isOnWaitinglist: reg.isOnWaitinglist || false,
              waitinglistExpires: reg.waitinglistExpires
                ? reg.waitinglistExpires instanceof Date
                  ? reg.waitinglistExpires
                  : typeof (reg.waitinglistExpires as any).toDate === "function"
                    ? (reg.waitinglistExpires as any).toDate()
                    : new Date(reg.waitinglistExpires)
                : null,
              termsAccepted: reg.termsAccepted || false,
            }));
            setIsEditingExisting(true);
            setExistingRegistrationId(reg.id ?? null);
          } else {
            if (!isMounted) return;
            setIsEditingExisting(false);
            setExistingRegistrationId(null);
            setFormData(prev => ({
              ...initialFormData,
              email: user.email || "",
              isOnWaitinglist: prev.isOnWaitinglist,
              waitinglistExpires: prev.waitinglistExpires
            }));
          }
        } catch (err: any) {
          if (err && (err.code === 'permission-denied' || String(err.code || '').includes('permission'))) {
            if (!isMounted) return;
            // Fail-soft: if rules prevent reading registrations, continue without prefill.
            setIsEditingExisting(false);
            setExistingRegistrationId(null);
            setFormData(prev => ({
              ...initialFormData,
              email: user.email || "",
              isOnWaitinglist: prev.isOnWaitinglist,
              waitinglistExpires: prev.waitinglistExpires
            }));
          } else
          if (err && err.code === "unavailable") {
            setSnackbarMessage(
              t('registration.couldNotConnect'),
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          } else {
            console.error("Error fetching existing registration:", err);
          }
        } finally {
          if (isMounted) setPrefillChecked(true);
        }
      } else {
        if (!isMounted) return;
        setIsEditingExisting(false);
        setExistingRegistrationId(null);
        setPrefillChecked(true);
      }
    };

    fetchAndPrefill();

    return () => {
      isMounted = false;
    };
  }, [
    authChecked,
    user,
    setSnackbarMessage,
    setSnackbarSeverity,
    setSnackbarOpen,
    t,
    event.id
  ]);

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthChecked(true);
      setProfilePrefillChecked(false);
      if (u && u.email) {
        setFormData((prev: any) => ({ ...prev, email: u.email }));
      }
    });
    return unsub;
  }, []);

  type FirestoreUserProfile = {
    uid?: string | null;
    email?: string;
    firstName?: string;
    lastName?: string;
    nationality?: string;
    dateOfBirth?: any;
    phoneCountryCode?: string | null;
    phone?: string | null;
    representing?: string[] | string | null;
    nfifLicenseNumber?: string | null;
    languagePreference?: string;
    personId?: number | string | null;
  };

  const normalizeEmail = (value: string) => value.trim().toLowerCase();

  const normalizePhoneCountryCode = (value: string | null | undefined): string => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) return '';
    return trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/^\+/, '')}`;
  };

  const normalizePhoneDigits = (value: string | null | undefined): string => {
    return String(value ?? '').replace(/\D/g, '').slice(0, 15);
  };

  const getLicenseYear = (licenseNumber: string): number | null => {
    const trimmed = String(licenseNumber ?? '').trim();
    if (!LICENSE_NUMBER_REGEX.test(trimmed)) return null;
    const parts = trimmed.split('-');
    const year = Number(parts[1]);
    return Number.isFinite(year) ? year : null;
  };

  const extractRepresentingValue = (value: unknown): string => {
    if (Array.isArray(value)) {
      const arr = value.map((v) => String(v ?? '').trim()).filter(Boolean);
      return arr.length ? arr[arr.length - 1] : '';
    }
    return String(value ?? '').trim();
  };

  const extractRepresentingOptions = (value: unknown): string[] => {
    const arr = Array.isArray(value)
      ? value.map((v) => String(v ?? '').trim()).filter(Boolean)
      : (typeof value === 'string' && value.trim())
        ? [value.trim()]
        : [];
    return Array.from(new Set(arr));
  };

  const resolveUserProfileDoc = useCallback(async (uid: string, _email?: string | null) => {
    const snapshot = await resolveAuthUserDocument(db, uid);
    return snapshot ? { id: snapshot.id, data: snapshot.data() as FirestoreUserProfile } : null;
  }, []);

  // Prefill from runner profile when user is logged in and has no existing registration for this edition.
  useEffect(() => {
    if (!authChecked || !prefillChecked || !user || !user.uid) {
      return;
    }
    if (isEditingExisting) {
      return;
    }
    if (profilePrefillChecked) {
      return;
    }

    let isMounted = true;
    const loadProfilePrefill = async () => {
      try {
        const resolved = await resolveUserProfileDoc(user.uid, user.email);
        if (!isMounted) return;
        if (!resolved) {
          return;
        }

        const profile = resolved.data;
        const normalizedPhoneCountryCode = normalizePhoneCountryCode(profile.phoneCountryCode ?? null);
        const normalizedPhone = normalizePhoneDigits(profile.phone ?? null);
        const normalizedDob = toDate(profile.dateOfBirth);
        const representingValue = extractRepresentingValue(profile.representing);
        const representingOpts = extractRepresentingOptions(profile.representing);
        const storedLicenseNumber = String(profile.nfifLicenseNumber ?? '').trim();
        const storedLicenseYear = getLicenseYear(storedLicenseNumber);
        const currentEventYear = eventYear;

        setRepresentingOptions(representingOpts);

        setFormData((prev: any) => {
          const next: any = { ...prev };
          if (!next.firstName && profile.firstName) next.firstName = profile.firstName;
          if (!next.lastName && profile.lastName) next.lastName = profile.lastName;

          if ((!next.nationality || next.nationality === initialFormData.nationality) && profile.nationality) {
            next.nationality = profile.nationality;
          }

          if (!next.dateOfBirth && normalizedDob) next.dateOfBirth = normalizedDob;

          if ((next.phoneCountryCode === initialFormData.phoneCountryCode || !next.phoneCountryCode) && normalizedPhoneCountryCode) {
            next.phoneCountryCode = normalizedPhoneCountryCode;
          }
          if (!next.phoneNumber && normalizedPhone) next.phoneNumber = normalizedPhone;
          if (!next.representing && representingValue) next.representing = representingValue;

          // Prefill language preference if not already set
          if (!next.languagePreference && profile.languagePreference) {
            next.languagePreference = profile.languagePreference;
          }

          // Prefill NFIF year license only if the stored license number is for the current event year.
          if (
            storedLicenseNumber &&
            storedLicenseYear === currentEventYear &&
            next.hasYearLicense === initialFormData.hasYearLicense &&
            String(next.licenseNumber ?? '').trim() === ''
          ) {
            next.hasYearLicense = true;
            next.licenseNumber = storedLicenseNumber;
          }

          return next;
        });
      } catch (err) {
        console.warn('[RegistrationPage] Failed to prefill from runner profile', err);
      } finally {
        if (isMounted) setProfilePrefillChecked(true);
      }
    };

    loadProfilePrefill();
    return () => {
      isMounted = false;
    };
  }, [authChecked, eventYear, prefillChecked, user, isEditingExisting, profilePrefillChecked, resolveUserProfileDoc]);
  useEffect(() => {
    if (!authChecked || !prefillChecked || !user || !user.uid) {
      return;
    }
    if (!isEditingExisting) {
      return;
    }
    if (formData.hasYearLicense !== true) {
      return;
    }
    if (String(formData.licenseNumber ?? '').trim() !== '') {
      return;
    }

    let cancelled = false;
    const loadLicense = async () => {
      try {
        const resolved = await resolveUserProfileDoc(user.uid, user.email);
        if (cancelled || !resolved) {
          return;
        }

        const profile = resolved.data;
        const storedLicenseNumber = String(profile.nfifLicenseNumber ?? '').trim();
        const storedLicenseYear = getLicenseYear(storedLicenseNumber);
        if (!storedLicenseNumber || storedLicenseYear !== eventYear) {
          return;
        }

        setFormData((prev: any) => {
          if (prev.hasYearLicense !== true) {
            return prev;
          }
          if (String(prev.licenseNumber ?? '').trim() !== '') {
            return prev;
          }
          return {
            ...prev,
            licenseNumber: storedLicenseNumber,
          };
        });
      } catch (err) {
        console.warn('[RegistrationPage] Failed to prefill stored NFIF license number for existing registration', err);
      }
    };

    loadLicense();
    return () => {
      cancelled = true;
    };
  }, [authChecked, prefillChecked, user, user?.email, isEditingExisting, formData.hasYearLicense, formData.licenseNumber, eventYear, resolveUserProfileDoc]);

  // Always load representing options for the Club dropdown when logged in.
  useEffect(() => {
    if (!authChecked || !user?.uid) return;
    let isMounted = true;
    const loadRepresenting = async () => {
      try {
        const resolved = await resolveUserProfileDoc(user.uid, user.email);
        if (!isMounted) return;
        const opts = extractRepresentingOptions(resolved?.data?.representing);
        setRepresentingOptions(opts);
      } catch {
        // ignore
      }
    };
    loadRepresenting();
    return () => {
      isMounted = false;
    };
  }, [authChecked, user, resolveUserProfileDoc]);

  const upsertRunnerProfileFromForm = useCallback(async () => {
    if (!user?.uid) {
      return;
    }
    const resolved = await resolveUserProfileDoc(user.uid, user.email);
    const docId = resolved?.id || user.uid;
    const existing = resolved?.data;
    const existingRepresenting = existing?.representing;
    const representingArr = Array.isArray(existingRepresenting)
      ? existingRepresenting.map((v) => String(v ?? '').trim()).filter(Boolean)
      : (typeof existingRepresenting === 'string' && existingRepresenting.trim())
        ? [existingRepresenting.trim()]
        : [];

    const newRepresenting = String(formData.representing ?? '').trim();
    const mergedRepresenting = newRepresenting && !representingArr.includes(newRepresenting)
      ? [...representingArr, newRepresenting]
      : representingArr;

    const payload: Record<string, any> = {
      uid: user.uid,
      email: normalizeEmail(String(user.email || formData.email || '')),
      firstName: String(formData.firstName || '').trim(),
      lastName: String(formData.lastName || '').trim(),
      nationality: String(formData.nationality || '').trim(),
      dateOfBirth: formData.dateOfBirth ?? null,
      phoneCountryCode: normalizePhoneCountryCode(formData.phoneCountryCode),
      phone: normalizePhoneDigits(formData.phoneNumber),
      languagePreference: formData.languagePreference || 'en',
      updatedAt: serverTimestamp()
    };
    if (mergedRepresenting.length) {
      payload.representing = mergedRepresenting;
    }


    await setDoc(doc(db, 'users', docId), payload, { merge: true });
  }, [user, formData, resolveUserProfileDoc]);

  const upsertRunnerLicenseFromForm = useCallback(async () => {
    if (!user?.uid) return;

    const licenseNumber = String(formData.licenseNumber ?? '').trim();
    if (formData.hasYearLicense !== true) return;
    const licenseYear = getLicenseYear(licenseNumber);
    if (!licenseYear) return;

    const resolved = await resolveUserProfileDoc(user.uid, user.email);
    const docId = resolved?.id || user.uid;

    await setDoc(
      doc(db, 'users', docId),
      {
        nfifLicenseNumber: licenseNumber,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }, [user, formData.hasYearLicense, formData.licenseNumber, resolveUserProfileDoc]);

  useEffect(() => {
    if (authChecked && !user) {
      navigate("/auth?returnTo=/register", { replace: true });
    }
  }, [authChecked, user, navigate]);

  // Check pre-registration eligibility when user is authenticated
  useEffect(() => {
    if (!authChecked || !user?.uid) {
      setEligibilityChecked(true);
      return;
    }

    // Only check eligibility if event is in pre_registration status
    if (!isPreRegistrationActive(event.status)) {
      setEligibilityChecked(true);
      setEligibilityResult({ eligible: true }); // Not in pre-registration, everyone eligible
      return;
    }

    // Skip eligibility check if editing existing registration
    if (isEditingExisting) {
      setEligibilityChecked(true);
      setEligibilityResult({ eligible: true });
      return;
    }

    // Check eligibility for pre-registration
    const checkEligibility = async () => {
      try {
        // Derive eventId from edition ID if not explicitly set
        // e.g., "kutc-2026" → "kutc"
        let eventIdToCheck = event.eventId;
        if (!eventIdToCheck && event.id) {
          const match = event.id.match(/^([a-z]+)-/i);
          if (match) {
            eventIdToCheck = match[1].toLowerCase();
          }
        }
        
        console.log('[RegistrationPage] Checking eligibility with eventId:', eventIdToCheck, 'from event:', event);
        
        const result = await checkPreRegistrationEligibility(
          user.uid,
          eventIdToCheck || event.id, // Fallback to full ID if derivation fails
          event.id
        );
        setEligibilityResult(result);
      } catch (error) {
        console.error('Error checking pre-registration eligibility:', error);
        setEligibilityResult({
          eligible: false,
          reason: 'Error checking eligibility',
        });
      } finally {
        setEligibilityChecked(true);
      }
    };

    checkEligibility();
  }, [authChecked, user, event.status, event.eventId, event.id, isEditingExisting]);

  const isLoggedIn = Boolean(user);

  // Check if user is blocked from pre-registration
  const isBlockedByPreRegistration = isPreRegistrationActive(event.status) && 
    eligibilityChecked && 
    eligibilityResult && 
    !eligibilityResult.eligible &&
    !isEditingExisting;

  const toDate = (value: any): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value.toDate === "function") return value.toDate();
    return new Date(value);
  };

  // Check if registration is still open / event has started
  const now = new Date();
  const eventStartDate = toDate(event.startTime);
  const registrationDeadlineDate = toDate(event.registrationDeadline);
  const hasEventStarted = eventStartDate ? now >= eventStartDate : false;
  const isRegistrationOpen = registrationDeadlineDate
    ? now < registrationDeadlineDate
    : !hasEventStarted;
  const canEditExisting = isEditingExisting && !hasEventStarted;
  const isRegistrationAccessible = isRegistrationOpen || canEditExisting;

  // Check if the form is valid for final submission (including terms)
  const isFormValidForSubmission = () => {
    const errors = validateAll(formData, validationContext, { showAllErrors: true });
    if (!formData.termsAccepted) errors.termsAccepted = "You must accept the terms and conditions";
    if (!user || !user.email) return false;
    return Object.keys(errors).length === 0;
  };

  const isFinalStep = activeStep === steps.length - 1;
  const submitDisabled =
    isSubmitting ||
    (isFinalStep &&
      (!isFormValidForSubmission() ||
        (isFull &&
          !isEditingExisting &&
          !formData.waitinglistExpires)));

  // Get all validation errors across all steps, grouped by step
  const getAllStepErrors = (): { stepIndex: number; stepName: string; errors: Record<string, string> }[] => {
    const allStepErrors: { stepIndex: number; stepName: string; errors: Record<string, string> }[] = [];
    
    // Step 0: Personal Info
    const personalInfoErrors = validatePersonalInfo(formData, { showAllErrors: true }, validationContext);
    if (Object.keys(personalInfoErrors).length > 0) {
      allStepErrors.push({
        stepIndex: 0,
        stepName: steps[0],
        errors: personalInfoErrors
      });
    }
    
    // Step 1: Race Details
    const raceDetailsErrors = validateRaceDetails(formData, validationContext, { showAllErrors: true });
    if (Object.keys(raceDetailsErrors).length > 0) {
      allStepErrors.push({
        stepIndex: 1,
        stepName: steps[1],
        errors: raceDetailsErrors
      });
    }
    
    // Step 2: Review & Submit
    const reviewErrors = validateReviewSubmit(formData, { showAllErrors: true });
    if (Object.keys(reviewErrors).length > 0) {
      allStepErrors.push({
        stepIndex: 2,
        stepName: steps[2],
        errors: reviewErrors
      });
    }
    
    return allStepErrors;
  };

  // Calculate all step errors for display (only on final step)
  const allStepErrors = isFinalStep ? getAllStepErrors() : [];
  const hasAnyErrors = allStepErrors.length > 0;

  const submitBlockedReasons: string[] = [];
  if (isFinalStep) {
    if (formData.notifyFutureEvents !== true && formData.notifyFutureEvents !== false) {
      submitBlockedReasons.push(t('registration.submitBlock.notifyFutureEvents'));
    }
    if (formData.sendRunningOffers !== true && formData.sendRunningOffers !== false) {
      submitBlockedReasons.push(t('registration.submitBlock.sendRunningOffers'));
    }
    if (formData.termsAccepted !== true) {
      submitBlockedReasons.push(t('registration.submitBlock.termsAccepted'));
    }
    if (isFull && !isEditingExisting && !formData.waitinglistExpires) {
      submitBlockedReasons.push(t('registration.submitBlock.waitinglistExpires'));
    }
  }

  const handleNext = () => {
    // Mark all fields for the current step as touched
    const updatedTouchedFields = markStepFieldsTouched(activeStep, touchedFields);
    setTouchedFields(updatedTouchedFields);
    setValidationAttempted(true);
    
    // Validate current step to show errors and update step indicator
    let currentStepErrors: Record<string, string> = {};
    if (activeStep === 0) {
      currentStepErrors = validatePersonalInfo(formData, { showAllErrors: true }, validationContext);
    } else if (activeStep === 1) {
      currentStepErrors = validateRaceDetails(formData, validationContext, { showAllErrors: true });
    }
    
    // Set errors to show visual feedback on current step
    setErrors(currentStepErrors);
    
    // Calculate next step
    const nextStep = activeStep + 1;
    
    // Mark next step as visited
    setVisitedSteps(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(nextStep);
      return newSet;
    });
    
    // ALWAYS navigate to next step (free navigation)
    setActiveStep(nextStep);
    window.scrollTo(0, 0);
    
    // Validate all visited steps to update step indicators
    setTimeout(() => {
      validateVisitedSteps();
      showCurrentStepErrors();
    }, 100);
  };

  // Scroll to the first error field
  const scrollToFirstError = (currentErrors: Record<string, string>) => {
    const errorFields = Object.keys(currentErrors);
    if (errorFields.length > 0) {
      const firstErrorField = errorFields[0];

      // Try input refs first
      const inputRef =
        fieldRefs.current[firstErrorField as keyof typeof fieldRefs.current];
      if (inputRef) {
        inputRef.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      // Try div refs if input ref not found
      const divRef = divRefs.current[firstErrorField];
      if (divRef?.current) {
        divRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  // Handle step click - FREE NAVIGATION to any step
  const handleStepClick = (stepIndex: number) => {
    // Mark the target step as visited
    setVisitedSteps(prev => {
      const newSet = new Set(Array.from(prev));
      newSet.add(stepIndex);
      return newSet;
    });
    
    // Navigate to the step
    setActiveStep(stepIndex);
    window.scrollTo(0, 0);
    
    // If navigating to a step that has errors, mark all fields for that step as touched
    // so the error states are immediately visible on form fields
    if (stepErrors[stepIndex]) {
      const updatedTouchedFields = markStepFieldsTouched(stepIndex, touchedFields);
      setTouchedFields(updatedTouchedFields);
    }
    
    // Validate all visited steps to update step indicators
    // Use setTimeout to ensure state is updated before validation
    setTimeout(() => {
      validateVisitedSteps();
      showCurrentStepErrors();
    }, 100);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    // Scroll to top when moving to previous step
    window.scrollTo(0, 0);

    // When going back, keep validation attempted state but show errors for the previous step
    setTimeout(() => showCurrentStepErrors(), 100);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  // Handle refund dialog close - navigate after user acknowledges
  const handleRefundDialogClose = () => {
    setRefundDialogOpen(false);
    if (pendingNavigation) {
      navigate(pendingNavigation, { state: { registrationSuccess: true } });
    }
  };

  // Store form data in localStorage when it changes
  useEffect(() => {
    const dataToStore = { ...formData };
    localStorage.setItem("registrationFormData", JSON.stringify(dataToStore));
  }, [formData]);

  // Load form data from Firestore draft (takes priority over localStorage)
  useEffect(() => {
    if (isEditingExisting || isDraftLoading) return;
    
    if (loadedDraft && loadedDraft.formData) {
      // Merge with initialFormData to ensure all required fields exist
      setFormData(prev => ({
        ...prev,
        ...loadedDraft.formData,
        // Ensure Date objects are properly converted
        dateOfBirth: loadedDraft.formData.dateOfBirth instanceof Date 
          ? loadedDraft.formData.dateOfBirth 
          : loadedDraft.formData.dateOfBirth 
            ? new Date(loadedDraft.formData.dateOfBirth) 
            : null,
        waitinglistExpires: loadedDraft.formData.waitinglistExpires instanceof Date
          ? loadedDraft.formData.waitinglistExpires
          : loadedDraft.formData.waitinglistExpires
            ? new Date(loadedDraft.formData.waitinglistExpires)
            : null,
        // If the event is already full, never let a stale draft reset the waitinglist flag
        ...(isFull ? { isOnWaitinglist: true } : {})
      }));
      setActiveStep(loadedDraft.currentStep || 0);
      setVisitedSteps(new Set(loadedDraft.visitedSteps || [0]));
    }
  }, [loadedDraft, isEditingExisting, isDraftLoading]);

  // Fallback: Load form data from localStorage on component mount, ONLY if not editing existing and no Firestore draft
  useEffect(() => {
    // If we are editing an existing registration, don't load from local storage
    // as the fetchAndPrefill effect handles prefilling from the database.
    if (isEditingExisting) {
      return; // Skip loading from localStorage
    }
    
    // If we have a Firestore draft, don't use localStorage
    if (loadedDraft) {
      return;
    }

    const savedFormData = localStorage.getItem("registrationFormData");
    if (savedFormData) {
      try {
        const parsedData = JSON.parse(savedFormData);
        // Convert date string back to Date object if it exists
        if (parsedData.dateOfBirth) {
          parsedData.dateOfBirth = new Date(parsedData.dateOfBirth);
        }
        // Convert waitinglistExpires string back to Date object if it exists
        if (parsedData.waitinglistExpires) {
          parsedData.waitinglistExpires = new Date(
            parsedData.waitinglistExpires,
          );
        }
        setFormData(parsedData);
      } catch (error) {
        console.error("Error parsing saved form data:", error);
        // Clear potentially corrupted local storage
        localStorage.removeItem("registrationFormData");
      }
    }
  }, [isEditingExisting, loadedDraft]); // Add loadedDraft dependency

  // Track previous activeStep to detect step navigation
  const prevActiveStepRef = useRef(activeStep);
  
  // Effect to show validation errors ONLY when navigating to a visited step with errors
  // Not when stepErrors changes within the same step (which would cause flashing)
  useEffect(() => {
    const navigatedToNewStep = prevActiveStepRef.current !== activeStep;
    prevActiveStepRef.current = activeStep;
    
    // Only show all errors when user navigates TO a step that was visited and has errors
    if (navigatedToNewStep && visitedSteps.has(activeStep) && stepErrors[activeStep]) {
      const updatedTouchedFields = markStepFieldsTouched(activeStep, touchedFields);
      setTouchedFields(updatedTouchedFields);
      showCurrentStepErrors();
    } else if (navigatedToNewStep && !validationAttempted && !visitedSteps.has(activeStep)) {
      // Clear errors only if validation hasn't been attempted AND step not visited
      clearAllErrors();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, visitedSteps, stepErrors]);

  const handleSubmit = async () => {
    // Validate the entire form including terms and conditions
    setValidationAttempted(true);
    const currentErrors = validateAll(formData, validationContext, { showAllErrors: true });

    // Require waiting-list expiration date if event is full (isOnWaitinglist is set automatically — no checkbox needed)
    if (isFull && !isEditingExisting) {
      if (!formData.waitinglistExpires) {
        setErrors((prev) => ({
          ...prev,
          waitinglistExpires: t('registration.selectExpirationDate'),
        }));
        setSnackbarMessage(t('registration.selectWaitlistExpiration'));
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        fieldRefs.current.waitinglistExpires?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }
    }

    // Only submit if the form is completely valid
    if (isFormValidForSubmission()) {
      // First test the Firestore connection
      const isConnected = await testFirestoreConnection();
      if (!isConnected) {
        setSnackbarMessage(
          t('registration.errorConnecting'),
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      try {
        setIsSubmitting(true);
        // Prepare registration data with trimmed text fields
        // Always use the current calculated payment amount
        const currentPaymentRequired = calculatedPaymentRequired > 0 ? calculatedPaymentRequired : (formData.paymentRequired ?? 0);
        
        const registrationData = {
          firstName: formData.firstName?.trim() || "",
          lastName: formData.lastName?.trim() || "",
          dateOfBirth: formData.dateOfBirth,
          nationality: formData.nationality,
          email: formData.email?.trim() || "",
          phoneCountryCode: formData.phoneCountryCode,
          phoneNumber: formData.phoneNumber?.trim() || "",
          representing: formData.representing?.trim() || "",
          languagePreference: formData.languagePreference || 'en',
          raceDistance: formData.raceDistance,
          travelRequired: formData.travelRequired?.trim() || "",
          termsAccepted: formData.termsAccepted === true,
          comments: formData.comments?.trim() || "",
          // Include marketing preferences
          notifyFutureEvents: formData.notifyFutureEvents ?? false,
          sendRunningOffers: formData.sendRunningOffers ?? false,
          // Include edition ID
          editionId: formData.editionId || event.id,
          // Waiting list info — derive directly from isFull so a stale draft value cannot block submission
          isOnWaitinglist: (isFull && !isEditingExisting) ? true : formData.isOnWaitinglist,
          waitinglistExpires: formData.waitinglistExpires,
          paymentRequired: currentPaymentRequired,
          paymentMade: formData.paymentMade ?? 0,
          // Add flag for license validation
          _requiresLicense: requiresLicense,
          // License info - only include if license is required for this race
          // Use null instead of undefined for Firestore compatibility
          ...(requiresLicense ? {
            hasYearLicense: formData.hasYearLicense ?? false,
            licenseNumber: formData.licenseNumber?.trim() || "",
          } : {}),
        };
        // Re-verify capacity at submit time — if the event filled while the form was being completed,
        // pause submission and let the user decide whether to join the waitinglist or cancel.
        if (!isEditingExisting && !registrationData.isOnWaitinglist && (event.maxParticipants ?? 0) > 0) {
          const { countActiveParticipants } = await import('../services/registrationService');
          const currentCount = await countActiveParticipants(event.id);
          if (currentCount >= (event.maxParticipants ?? 0)) {
            // Update isFull so the isFull-useEffect will set formData.isOnWaitinglist=true
            // before the next submit attempt (if user chooses to join the waitinglist).
            setIsFull(true);
            setOpenRegCount(currentCount);
            setForcedWaitlistDialogOpen(true);
            setIsSubmitting(false);
            return; // Pause — the user must confirm before registration is created
          }
        }

        let registrationId = existingRegistrationId;
        if (isEditingExisting && existingRegistrationId) {
          // Update
          const { updateRegistration, getRegistrationById } = await import(
            "../services/registrationService"
          );
          
          // Don't send email yet - we'll send it after payment processing
          await updateRegistration(existingRegistrationId, registrationData, false);

          try {
            await upsertRunnerLicenseFromForm();
          } catch (err) {
            console.error('[RegistrationPage] Failed to store NFIF license number', err);
          }

          if (updateRunnerProfile) {
            try {
              await upsertRunnerProfileFromForm();
            } catch (err) {
              console.error('[RegistrationPage] Failed to update runner profile from registration', err);
            }
          }

          // Check if there's an outstanding amount to pay
          const updatedReg = await getRegistrationById(existingRegistrationId);
          const paymentRequired = updatedReg?.paymentRequired || 0;
          const paymentMade = updatedReg?.paymentMade || 0;
          const outstandingAmount = Math.max(0, paymentRequired - paymentMade);
          
          if (outstandingAmount > 0) {
            // There's an outstanding amount - initiate payment
            try {
              setSnackbarMessage(t('registration.redirectingToPayment'));
              setSnackbarSeverity("info");
              setSnackbarOpen(true);
              
              const { redirectToStripeCheckout } = await import('../services/stripeService');
              await redirectToStripeCheckout(existingRegistrationId, formData.languagePreference || 'en');
              return; // Don't show success message, redirect to payment
            } catch (paymentError) {
              console.error('[RegistrationPage] Payment redirect failed:', paymentError);
              // Send registration update email since payment failed
              const { sendRegistrationUpdateEmail } = await import('../services/emailService');
              if (updatedReg) {
                await sendRegistrationUpdateEmail(updatedReg);
              }
              setSnackbarMessage(t('registration.registrationUpdatedPaymentFailed'));
              setSnackbarSeverity("warning");
            }
          } else {
            // No outstanding amount - send registration update email
            const { sendRegistrationUpdateEmail } = await import('../services/emailService');
            if (updatedReg) {
              await sendRegistrationUpdateEmail(updatedReg);
            }
            
            // Check if there's an overpayment (refund situation)
            const overpayment = paymentMade - paymentRequired;
            if (overpayment > 0) {
              // Check if refund is below threshold
              if (updatedReg?.refundBelowThreshold) {
                const threshold = updatedReg.refundBelowThreshold;
                setRefundDialogMessage(
                  t('registration.refundBelowThreshold', {
                    grossAmount: threshold.grossAmount,
                    transactionFee: threshold.transactionFee,
                    netAmount: threshold.netAmount,
                    minimumRequired: threshold.minimumRequired
                  })
                );
              } else {
                // Refund will be evaluated - show dialog requiring user acknowledgment
                setRefundDialogMessage(t('registration.refundWillBeEvaluated', { amount: overpayment }));
              }
              localStorage.removeItem("registrationFormData");
              await deleteDraft(); // Remove draft after successful update
              // Get personId for navigation after dialog closes
              const resolved = await resolveUserProfileDoc(user.uid, user.email);
              const personId = resolved?.data?.personId;
              setPendingNavigation(personId ? `/runners/${personId}` : `/${event.id}`);
              setRefundDialogOpen(true);
              setIsSubmitting(false);
              return; // Don't continue with normal flow
            } else {
              setSnackbarMessage(t('registration.registrationUpdated'));
              setSnackbarSeverity("success");
            }
          }
        } else {
          const { createRegistration, getRegistrationsByUserId } = await import(
            "../services/registrationService"
          );

          // Check for existing registration for this user (fail-soft on permission denied)
          let existingRegs: any[] = [];
          try {
            existingRegs = await getRegistrationsByUserId(user.uid, event.id);
          } catch (err: any) {
            if (!(err && (err.code === 'permission-denied' || String(err.code || '').includes('permission')))) {
              throw err;
            }
          }
          if (existingRegs && existingRegs.length > 0) {
            setSnackbarMessage(
              t('registration.duplicateRegistration'),
            );
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setIsSubmitting(false);
            return;
          }

          registrationId = await createRegistration(
            registrationData,
            user?.uid,
          );

          // Track registration completion in analytics
          rhAnalytics.trackRegistrationEvent('registration_complete', {
            eventId: event.id,
            registrationId: registrationId || undefined
          });

          try {
            await upsertRunnerLicenseFromForm();
          } catch (err) {
            console.error('[RegistrationPage] Failed to store NFIF license number', err);
          }

          if (updateRunnerProfile) {
            try {
              await upsertRunnerProfileFromForm();
            } catch (err) {
              console.error('[RegistrationPage] Failed to update runner profile from registration', err);
            }
          }

          // Handle payment if required
          if (registrationData.paymentRequired > 0) {
            try {
              // Registration has been created successfully; remove draft before redirecting to payment
              localStorage.removeItem("registrationFormData");
              await deleteDraft();

              // Show redirect message
              setSnackbarMessage(t('registration.redirectingToPayment'));
              setSnackbarSeverity("info");
              setSnackbarOpen(true);
              
              const { redirectToStripeCheckout } = await import('../services/stripeService');
              await redirectToStripeCheckout(registrationId, formData.languagePreference || 'en');
              return; // Don't show success message, redirect to payment
            } catch (paymentError) {
              console.error('[RegistrationPage] Payment failed:', paymentError);
              // Registration succeeded but payment failed
              setSnackbarMessage(t('registration.registrationSuccessPaymentFailed'));
              setSnackbarSeverity("warning");
            }
          }

          if (registrationData.isOnWaitinglist) {
            setSnackbarMessage(t('registration.waitlistSuccess'));
          } else {
            setSnackbarMessage(t('registration.registrationSuccess'));
          }
          setSnackbarSeverity("success");
        }
        setSnackbarOpen(true);
        localStorage.removeItem("registrationFormData");
        if (registrationId) {
          await deleteDraft(); // Remove draft after successful registration
        }
        setTimeout(async () => {
          // Navigate to the Runner's page after registration/update
          try {
            const resolved = await resolveUserProfileDoc(user.uid, user.email);
            const personId = resolved?.data?.personId;
            if (personId) {
              navigate(`/runners/${personId}`, {
                state: { registrationSuccess: true, registrationId },
              });
            } else {
              // Fallback to event page if no personId
              navigate(`/${event.id}`, {
                state: { registrationSuccess: true, registrationId },
              });
            }
          } catch (err) {
            console.error('[RegistrationPage] Error navigating to runner page:', err);
            navigate(`/${event.id}`, {
              state: { registrationSuccess: true, registrationId },
            });
          }
        }, 2000);
      } catch (error) {
        console.error("Error submitting registration:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        setSnackbarMessage(`${t('registration.errorSubmitting')}: ${errorMessage}`);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setIsSubmitting(false);
        return;
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Show error message
      setErrors(currentErrors);
      setSnackbarMessage(t('registration.completeFieldsBeforeSubmitting'));
      setSnackbarOpen(true);
      setSnackbarSeverity("error");
      scrollToFirstError(currentErrors);
    }
  };

  // Restore getStepContent so it is in scope for rendering
  const getStepContent = (step: number) => {
//    console.log("RegistrationPage - current event:", event);
    switch (step) {
      case 0:
        return (
          <PersonalInfoForm
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
            fieldRefs={divRefs.current}
            onBlur={handleFieldTouch}
            isEditingExisting={isEditingExisting}
            isFull={isFull}
            isEmailReadOnly={true}  /* Make email field read-only since it comes from auth */
            registrationConfig={registrationConfig}
            representingOptions={representingOptions}
          />
        );
      case 1:
        return (
          <RaceDetailsForm
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
            fieldRefs={divRefs.current}
            onBlur={handleFieldTouch}
            event={event}
            touchedFields={touchedFields}
          />
        );
      case 2:
        return (
          <ReviewRegistration
            event={event}
            formData={formData}
            onChange={handleFormChange}
            errors={errors}
            fieldRefs={divRefs.current}
            onBlur={handleFieldTouch}
            isEditingExisting={isEditingExisting}
            isFull={isFull}
          />
        );
      default:
        return "Unknown step";
    }
  };

  // Show loading while checking eligibility
  if (isLoggedIn && !eligibilityChecked) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 8, textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Checking eligibility...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Block access if user is not eligible for pre-registration
  if (isBlockedByPreRegistration) {
    const registrationOpensDate = toDate(event.registrationOpens);
    const registrationOpensText = registrationOpensDate 
      ? registrationOpensDate.toLocaleString(undefined, { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        })
      : 'soon';

    return (
      <Container maxWidth="md">
        <Box sx={{ my: 8 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Pre-registration Open for Previous Participants
            </Typography>
            <Typography variant="body1" paragraph>
              Registration is currently in the pre-registration phase, which is only available to participants 
              who have competed in previous editions of {event.eventName}.
            </Typography>
            <Typography variant="body1" paragraph>
              {eligibilityResult?.reason && (
                <strong>Eligibility status: {eligibilityResult.reason}</strong>
              )}
            </Typography>
            <Typography variant="body1">
              General registration will open on <strong>{registrationOpensText}</strong>.
            </Typography>
          </Alert>
          <Box sx={{ textAlign: "center" }}>
            <Button variant="contained" onClick={() => navigate(`/${event.id}`)}>
              Return to Event Page
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!isRegistrationAccessible) {
    if (isLoggedIn && !prefillChecked) {
      return (
        <Container maxWidth="md">
          <Box sx={{ my: 8, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    const messageTitle = hasEventStarted ? t('registration.eventStarted') : t('registration.registrationClosed');
    const messageBody = hasEventStarted
      ? t('registration.eventStartedMessage')
      : t('registration.registrationClosedMessage');

    return (
      <Container maxWidth="md">
        <Box sx={{ my: 8, textAlign: "center" }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {messageTitle}
          </Typography>
          <Typography variant="body1" paragraph>
            {messageBody}
          </Typography>
          <Button variant="contained" onClick={() => navigate("/")}>
            {t('registration.returnToHome')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <RegistrationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
      
      {/* Waitlist info dialog — shown once on page load when the event is already full */}
      <Dialog
        open={waitlistInfoDialogOpen}
        onClose={() => setWaitlistInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { mx: 2, width: 'calc(100% - 32px)' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('registration.waitlistInfoDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {t('registration.waitlistInfoDialogMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={() => { setWaitlistInfoDialogOpen(false); window.location.href = callerUrlRef.current; }}
            variant="outlined"
            color="inherit"
          >
            {t('registration.waitlistInfoDialogCancel')}
          </Button>
          <Button
            onClick={() => setWaitlistInfoDialogOpen(false)}
            variant="contained"
            color="warning"
          >
            {t('registration.waitlistInfoDialogConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Forced-waitinglist confirmation dialog — shown when the event fills up while the user is completing the form */}
      <Dialog
        open={forcedWaitlistDialogOpen}
        onClose={() => { setForcedWaitlistDialogOpen(false); }}
        maxWidth="sm"
        fullWidth
        sx={{ '& .MuiDialog-paper': { mx: 2, width: 'calc(100% - 32px)' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('registration.forcedWaitlistDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
            {t('registration.forcedWaitlistDialogMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setForcedWaitlistDialogOpen(false)}
            variant="outlined"
            color="inherit"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => { setForcedWaitlistDialogOpen(false); handleSubmit(); }}
            variant="contained"
            color="warning"
            autoFocus
          >
            {t('registration.joinWaitingList')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund acknowledgment dialog */}
      <Dialog 
        open={refundDialogOpen} 
        onClose={handleRefundDialogClose}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            mx: 2,
            width: 'calc(100% - 32px)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('registration.refundDialogTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ 
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            whiteSpace: 'pre-wrap'
          }}>
            {refundDialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRefundDialogClose} variant="contained" color="primary" autoFocus fullWidth>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "var(--color-surface)",
          color: "var(--color-text)",
          border: "1px solid var(--color-surface-border)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          borderRadius: 2,
          p: 3,
          mb: 4,
        }}
      >
        {/* Registration Stepper */}
        <RegistrationStepper
          steps={steps}
          activeStep={activeStep}
          onStepClick={handleStepClick}
          visitedSteps={visitedSteps}
          stepErrors={stepErrors}
        />
        <Typography variant="h4" component="h1" align="center" gutterBottom>
          <span>
            {event.eventName}
            {event.edition && ` ${event.edition}`}
            {' - '}
          </span>
          {isEditingExisting
            ? formData.isOnWaitinglist 
              ? t('registration.reviewingWaitlistRegistration')
              : t('registration.reviewingRegistration')
            : isFull
              ? t('registration.waitlistRegistration')
              : t('registration.title')}
        </Typography>
        
        {/* Spots remaining counter — shown when event is open and has a defined limit */}
        {!isFull && !isEditingExisting && (event.maxParticipants ?? 0) > 0 && (
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Alert
              severity={openRegCount >= (event.maxParticipants ?? 0) - 5 ? 'warning' : 'info'}
              sx={{ display: 'inline-flex', px: 2, py: 0.5 }}
            >
              {t('registration.spotsRemaining', { count: Math.max(0, (event.maxParticipants ?? 0) - openRegCount) })}
            </Alert>
          </Box>
        )}

        {/* Pending-registration notice — spot is not secured until payment is confirmed */}
        {!isFull && !isEditingExisting && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('registration.spotsPendingNotice')}
          </Alert>
        )}

        {/* Registration Help Section */}
        <RegistrationHelpSection />
        {isEditingExisting && (
          <React.Fragment>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('registration.updateInfo')}
            </Alert>
            <Box sx={{ mb: 2 }}>
              {(() => {
                const s = String((formData as any).status || 'pending').toLowerCase();
                const label = t(`registrationStatus.${s}`, { defaultValue: s });
                return (
                  <Alert severity={s === 'confirmed' ? 'success' : 'warning'}>
                    {t('registrationStatus.label')}: {label}
                    {formData.isOnWaitinglist ? ` (${t('events.onWaitlistCount')})` : ''}
                  </Alert>
                );
              })()}
            </Box>
            <Box sx={{ mb: 2 }}>
              <Alert
                severity={
                  formData.paymentMade >= (calculatedPaymentRequired > 0 ? calculatedPaymentRequired : formData.paymentRequired)
                    ? "success"
                    : formData.paymentMade < (calculatedPaymentRequired > 0 ? calculatedPaymentRequired : formData.paymentRequired)
                      ? "warning"
                      : "info"
                }
              >
                {t('status.label')}: {" "}
                {formData.paymentMade >= (calculatedPaymentRequired > 0 ? calculatedPaymentRequired : formData.paymentRequired)
                  ? t('registration.paymentMade')
                  : formData.paymentMade < (calculatedPaymentRequired > 0 ? calculatedPaymentRequired : formData.paymentRequired)
                    ? t('registration.paymentRequired')
                    : t('registration.paymentPending')}
              </Alert>
            </Box>
          </React.Fragment>
        )}
        {getStepContent(activeStep)}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          {activeStep !== 0 && (
            <Button onClick={handleBack} sx={{ mr: 2 }}>
              {t('registration.back')}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={
              activeStep === steps.length - 1 ? handleSubmit : handleNext
            }
            disabled={submitDisabled}
          >
            {activeStep === steps.length - 1 ? t('registration.submit') : t('registration.next')}
          </Button>
        </Box>
        {/* Enhanced error summary for final step */}
        {isFinalStep && submitDisabled && !isSubmitting && hasAnyErrors && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            backgroundColor: 'background.paper', 
            borderRadius: 1,
            border: '2px solid',
            borderColor: 'error.main'
          }}>
            <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('registration.submitDisabledHelp')}
            </Typography>
            {allStepErrors.map((stepError) => {
              const isCurrentStep = stepError.stepIndex === activeStep;
              return (
                <Box key={stepError.stepIndex} sx={{ mb: 1.5 }}>
                  {isCurrentStep ? (
                    <Typography 
                      variant="body2" 
                      color="text.primary"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {stepError.stepName}:
                    </Typography>
                  ) : (
                    <Typography 
                      variant="body2" 
                      color="primary.main"
                      sx={{ 
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        display: 'inline-flex',
                        alignItems: 'center',
                        '&:hover': { color: 'primary.dark' }
                      }}
                      onClick={() => handleStepClick(stepError.stepIndex)}
                    >
                      {stepError.stepName} →
                    </Typography>
                  )}
                  <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
                    {Object.entries(stepError.errors).map(([field, message]) => (
                      <Typography 
                        key={field} 
                        component="li" 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: '0.85rem' }}
                      >
                        {message}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

// Main RegistrationPage component
const RegistrationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { event, loading, error } = useEventEdition();
  // Sync EventEditionContext from URL query params (?series=... or ?editionId=...).
  // Makes entry points like /register?series=kutc work without prior navigation.
  const { resolving: urlResolving, resolveError: urlResolveError } = useEditionFromUrl();

  if (loading || urlResolving) {
    return (
      <Container
        maxWidth="md"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error || urlResolveError || !event) {
    const message = error
      ? `${t('errors.loadFailed')}: ${error.message}`
      : urlResolveError
        ? urlResolveError
        : t('errors.notFound');
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{message}</Alert>
        <Button
          variant="contained"
          onClick={() => navigate("/")}
          sx={{ mt: 2 }}
        >
          {t('errors.backToHome')}
        </Button>
      </Container>
    );
  }

  return <RegistrationPageInner event={event} />;
};

export default RegistrationPage;
